import { useState } from 'react'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  SectionLabel,
} from '../../../design/components'
import type { Note, NotesSection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

/**
 * The caller's private campaign notes. Section `notes`.
 *
 * Private means private: the loader only ever reads the caller's own rows, so
 * somebody else's note is not merely hidden from the response — it is not in the
 * graph the controller can reach, and its id 404s. There is therefore no "whose note
 * is this" affordance to build, and every note here is editable by whoever is reading.
 */
export function NotesCard({
  notes,
  editing,
}: {
  notes: NotesSection | undefined
  editing: CampaignEditing
}) {
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  if (notes === undefined) return null

  const entries = notes.notes ?? []

  return (
    <CollapsibleCard
      variant="bio"
      // "Notes", not "My notes": with a summary, a hint, a chevron and an action all in
      // one collapsed header, a two-word label wraps at 390px. The hint carries the
      // ownership, which is the part that actually needs saying.
      label="Notes"
      summary={entries.length === 1 ? '1 note' : `${entries.length} notes`}
      hint="Private to you"
      action={
        !adding ? (
          <IconButton label="Add a note" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {entries.length === 0 && !adding && <p className={styles.empty}>Nothing written down yet.</p>}

      {entries.map((note) =>
        editingId === note.id ? (
          <NoteForm
            key={note.id}
            note={note}
            editing={editing}
            onClose={() => setEditingId(null)}
          />
        ) : (
          <div key={note.id} className={styles.row}>
            <div className={styles.rowHead}>
              <span className={styles.rowName}>{note.title}</span>
            </div>
            {note.content !== '' && <p className={styles.rowText}>{note.content}</p>}
            <div className={styles.rowActions}>
              <Button variant="secondary" onClick={() => setEditingId(note.id)}>
                Edit
              </Button>
              <Button
                variant="danger"
                disabled={editing.removeNote.isPending}
                onClick={() => editing.removeNote.mutate({ noteId: note.id })}
              >
                Delete
              </Button>
            </div>
          </div>
        ),
      )}

      {adding && <NoteForm editing={editing} onClose={() => setAdding(false)} />}
    </CollapsibleCard>
  )
}

/** One form for both add and edit — the create and update bodies are the same shape. */
function NoteForm({
  note,
  editing,
  onClose,
}: {
  note?: Note | undefined
  editing: CampaignEditing
  onClose: () => void
}) {
  const [title, setTitle] = useState(note?.title ?? '')
  const [content, setContent] = useState(note?.content ?? '')

  const mutation = note === undefined ? editing.addNote : editing.updateNote
  const pending = mutation.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        const body = { title, content: content === '' ? null : content }

        if (note === undefined) {
          editing.addNote.mutate(body, { onSuccess: onClose })
        } else {
          editing.updateNote.mutate({ noteId: note.id, body }, { onSuccess: onClose })
        }
      }}
    >
      <SectionLabel tone="ember" micro>
        {note === undefined ? 'New note' : 'Edit note'}
      </SectionLabel>

      <Input label="Title" value={title} autoFocus onChange={(e) => setTitle(e.target.value)} />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Content
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || title.trim() === ''}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
